import { FC, useMemo } from "react";
import styled from "styled-components";
import { useNavigate } from "react-router-dom";

import Modal from "../modal/Modal";
import ThreadItem from "../thread/ThreadItem";
import type { Transaction } from "../../app/services/transactions";
import { amountLabelForTransaction } from "../../utils/transactions";

// プロジェクト名の見出し（進行中モーダルと同系統の控えめな表記）
const GroupHeader = styled.div.attrs({ className: "bodytext-13" })`
  color: var(--soremo-light-gray);
  padding: 8px 0 0 0;
`;

const Empty = styled.div`
  color: var(--soremo-light-gray);
  text-align: center;
  padding: 24px 0;
`;

// 残高（税込）のサマリ（リスト上部に固定的に見せる）。ラベルと金額を左右に広げる。
// 枠のスタイルは進行中モーダル（TransactionsModal）の SummaryBox と揃える。
const SummaryRow = styled.div.attrs({
  className: "u-row-between u-w100",
})`
  padding: 16px 12px;
  border: 1px solid var(--soremo-border);
  border-radius: 4px;
  background: var(--soremo-background);
`;

// 金額表示欄は進行中モーダル（TransactionsModal）の
// 「オファーに使える残高（税込）」と同じデザインに揃える。
const AvailableLabel = styled.span.attrs({ className: "heading-16" })``;

const TaxSuffix = styled.span.attrs({ className: "bodytext-11" })`
  color: var(--soremo-light-gray);
`;

const AvailableValue = styled.span.attrs({ className: "heading-18" })``;

const DoneText = styled.div.attrs({ className: "bodytext-13 u-line-height-150" })`
  color: var(--soremo-gray);
`;

interface WithdrawalRequestModalProps {
  /** 振込申請対象（受託者として検収完了・未申請）の取引 */
  transactions: Transaction[];
  /** 委託/受託の判定（ThreadItem 内）に使うログインユーザー ID */
  currentUserId?: number | null;
  /** 申請額の表示文字列（税込 ¥ 付き。支払明細書に紐づく settled ベース） */
  amountLabel: string;
  /** 申請 API 実行中か */
  isSubmitting: boolean;
  /** 申請が完了したか（完了画面に切り替える） */
  done: boolean;
  /** 申請失敗時のエラーメッセージ */
  error?: string | null;
  /** 完了画面で案内する振込先銀行名 */
  bankName?: string | null;
  /** 完了画面で案内する振込先支店名 */
  bankBranch?: string | null;
  /** 「振込申請 >」押下 */
  onSubmit: () => void;
  onClose: () => void;
}

/**
 * 振込申請の確認モーダル。
 *
 * 進行中モーダル（{@link TransactionsModal}）と同じ `ThreadItem` で
 * 「振込申請の対象になるオファー（受託者として検収完了・未申請）」を
 * プロジェクト毎にグループ化して一覧表示し、内容を確認した上で
 * 「振込申請 >」で申請する。金額は支払明細書に紐づく `balance_settled`
 * ベース（税込表示）で、申請額と一致する。
 */
const WithdrawalRequestModal: FC<WithdrawalRequestModalProps> = ({
  transactions,
  currentUserId,
  amountLabel,
  isSubmitting,
  done,
  error,
  bankName,
  bankBranch,
  onSubmit,
  onClose,
}) => {
  const navigate = useNavigate();

  // プロジェクト毎にグループ化（出現順を維持）
  const groups = useMemo(() => {
    const map = new Map<
      string,
      { projectName: string; items: Transaction[] }
    >();
    transactions.forEach((tx) => {
      const key = tx.project_id ?? "unknown";
      if (!map.has(key)) {
        map.set(key, {
          projectName: tx.project_name ?? "（プロジェクト名なし）",
          items: [],
        });
      }
      map.get(key)!.items.push(tx);
    });
    return Array.from(map.entries());
  }, [transactions]);

  const handleOpen = (tx: Transaction) => {
    if (tx.project_id) {
      navigate(`/${tx.project_id}?tab=dm&thread=${tx.id}`);
    }
    onClose();
  };

  if (done) {
    return (
      <Modal
        title="振込申請を受け付けました"
        onClose={onClose}
        size="default"
        primaryAction={{ label: "閉じる", onClick: onClose }}
      >
        <DoneText>
          5営業日を目安に、ご登録の{bankName}銀行
          {bankBranch}支店へお振込します。
          <br />
          詳しい内訳は、支払明細書でご確認いただけます。
        </DoneText>
      </Modal>
    );
  }

  const hasItems = transactions.length > 0;

  return (
    <Modal
      title="振込申請"
      onClose={onClose}
      size="default"
      primaryAction={{
        label: "銀行口座へ振込",
        onClick: onSubmit,
        rightIcon: "chevron_right",
        loading: isSubmitting,
        disabled: !hasItems || isSubmitting,
      }}
      secondaryAction={{
        label: "キャンセル",
        onClick: onClose,
        theme: "secondary",
        disabled: isSubmitting,
      }}
    >
      <SummaryRow>
        <span>
          <AvailableLabel>残高</AvailableLabel>
          <TaxSuffix>（税込）</TaxSuffix>
        </span>
        <AvailableValue>{amountLabel}</AvailableValue>
      </SummaryRow>

      {!hasItems ? (
        <Empty className="bodytext-13">
          振込申請の対象となる取引はありません
        </Empty>
      ) : (
        groups.map(([key, group]) => (
          <div key={key}>
            <GroupHeader>{group.projectName}</GroupHeader>
            {group.items.map((tx) => (
              <ThreadItem
                key={tx.id}
                thread={tx}
                currentUserId={currentUserId ?? undefined}
                unreadCount={tx.unread_count}
                amountLabel={amountLabelForTransaction(tx)}
                onClick={() => handleOpen(tx)}
              />
            ))}
          </div>
        ))
      )}

      {error && <span className="u-text-green">{error}</span>}
    </Modal>
  );
};

export default WithdrawalRequestModal;
